import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { websocketService } from '../services/websocketService';
import { showToast } from '../components/Toast';

// Owns every read and write behind the My Membership screen. The page itself is
// four tab renderers plus a sheet host, so all of the fetching, the WebSocket
// subscription, and the money actions live here.
//
// Loads are deliberately split into three waves. The core wave is what the Home
// tab needs to render at all and is awaited; the standing and assigned waves back
// tabs the member has not opened yet, so they settle on their own and each panel
// renders its own empty or error state rather than blocking the page.

const GUEST_PASS_COST = 15.0;

export const CREDIT_COST = 1.0;
export { GUEST_PASS_COST };

function useMyMembership() {
  const [currentUser, setCurrentUser] = useState(null);
  const [membershipSettings, setMembershipSettings] = useState(null);
  // What this member has signed and when. Their own record, so it belongs on
  // their profile rather than only in an admin view.
  const [signedDocuments, setSignedDocuments] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const [badges, setBadges] = useState([]);
  const [taskStats, setTaskStats] = useState(null);

  const [placements, setPlacements] = useState([]);
  const [golfSummary, setGolfSummary] = useState(null);
  const [matchplayProfile, setMatchplayProfile] = useState(null);

  const [assigned, setAssigned] = useState({ hosted: [], rsvps: [], tasks: [], issues: [] });
  const [assignedLoading, setAssignedLoading] = useState(true);

  const [busy, setBusy] = useState({});

  const setBusyFor = useCallback((key, value) => {
    setBusy((prev) => ({ ...prev, [key]: value }));
  }, []);

  const refreshUser = useCallback(async () => {
    const response = await apiService.getCurrentUser();
    setCurrentUser(response.data);
    return response.data;
  }, []);

  const loadTransactions = useCallback(async (userId) => {
    try {
      setTransactionsLoading(true);
      const response = await apiService.getCreditHistory(userId, { limit: 50 });
      const data = response.data.transactions || response.data;
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load transaction history:', err);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  // Standing and Assigned back tabs that are not the landing tab. Each promise
  // settles independently so one unreachable panel never blanks the others.
  const loadStanding = useCallback(async (user) => {
    apiService.getMyBadges()
      .then((res) => setBadges(res.data || []))
      .catch(() => setBadges([]));
    apiService.getMyTaskStats()
      .then((res) => setTaskStats(res.data))
      .catch(() => setTaskStats(null));
    apiService.getUserCompetitionPlacements(user.id)
      .then((res) => setPlacements(res.data || []))
      .catch(() => setPlacements([]));
    apiService.getMyPingolfSummary()
      .then((res) => setGolfSummary(res.data))
      .catch(() => setGolfSummary(null));

    if (user.matchplay_user_id) {
      apiService.get(`/users/${user.id}/matchplay-profile`)
        .then((res) => setMatchplayProfile(res.data))
        .catch(() => setMatchplayProfile(null));
    } else {
      setMatchplayProfile(null);
    }
  }, []);

  const loadAssigned = useCallback(async (user) => {
    setAssignedLoading(true);
    const [hosted, rsvps, tasks, issues] = await Promise.all([
      apiService.getMyHostedEvents().then((r) => r.data || []).catch(() => []),
      apiService.getMyRsvps().then((r) => r.data || []).catch(() => []),
      apiService.getTasks({ assignedToMe: true }).then((r) => r.data?.tasks || r.data || []).catch(() => []),
      // Tier 0 members are never assigned an issue, so skip the call rather than
      // fetching a list that is always empty for them.
      (user.tech_tier ?? 0) > 0
        ? apiService.getMachineIssues({ assignedTo: user.id, status: 'open' }).then((r) => r.data || []).catch(() => [])
        : Promise.resolve([])
    ]);

    setAssigned({ hosted, rsvps, tasks, issues });
    setAssignedLoading(false);
  }, []);

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      const user = await refreshUser();

      loadTransactions(user.id);

      if (!user.is_member) {
        apiService.getOnboardingStatus()
          .then((res) => setOnboardingStatus(res.data))
          .catch(() => setOnboardingStatus(null));
      } else {
        apiService.getMembershipSettings(user.id)
          .then((res) => setMembershipSettings(res.data))
          .catch(() => setMembershipSettings(null));

        apiService.getMySignedDocuments()
          .then((res) => setSignedDocuments(res.data || []))
          .catch(() => setSignedDocuments([]));
      }

      loadStanding(user);
      loadAssigned(user);
    } catch (err) {
      setError('Failed to load membership data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [refreshUser, loadTransactions, loadStanding, loadAssigned]);

  const loadDevices = useCallback(async () => {
    try {
      setDevicesLoading(true);
      const response = await apiService.getDeviceStatus();
      setDevices(response.data || []);
    } catch (err) {
      console.error('Failed to load devices:', err);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCore();
    loadDevices();

    // A machine going offline while the member is looking at it swaps that card
    // in place. Nothing else on the page re-renders.
    const unsubscribe = websocketService.on('device_status', (data) => {
      const { deviceId, isOnline, localIp, remoteIp, isStale } = data;
      setDevices((prev) => prev.map((device) => (
        device.id === deviceId
          ? {
              ...device,
              isOnline,
              local_ip: localIp || device.local_ip,
              remote_ip: remoteIp || device.remote_ip,
              connection: {
                ...(device.connection || {}),
                isStale: !!isStale,
                localIp: localIp || device.connection?.localIp || null,
                remoteIp: remoteIp || device.connection?.remoteIp || null
              }
            }
          : device
      )));
    });

    return unsubscribe;
  }, [loadCore, loadDevices]);

  // Transactions come back newest-first, so walk down from the current balance to
  // work out what the balance was immediately after each one.
  const transactionsWithBalance = useMemo(() => {
    let runningBalance = currentUser?.creditBalance ?? 0;
    return transactions.map((txn) => {
      const balanceAfter = runningBalance;
      runningBalance -= parseFloat(txn.amount);
      return { ...txn, balanceAfter };
    });
  }, [transactions, currentUser]);

  const liveDevices = useMemo(() => devices.filter((d) => d.is_active), [devices]);

  const balance = Number(currentUser?.creditBalance ?? 0);
  const tabEnabled = !!currentUser?.tab_enabled;
  const isNegative = balance < 0;
  // With no tab, a negative balance stops every purchase. With a tab, play carries
  // on and the balance simply goes further negative.
  const purchasesBlocked = isNegative && !tabEnabled;

  // Money actions. Each one settles the server call, refreshes the balance, and
  // reports through a toast. The caller closes its own sheet.
  const runPurchase = useCallback(async (busyKey, call, successMessage) => {
    try {
      setBusyFor(busyKey, true);
      await call();
      const user = await refreshUser();
      loadTransactions(user.id);
      showToast(successMessage, 'success');
      return true;
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || 'That did not go through';
      showToast(message, 'error');
      console.error(err);
      return false;
    } finally {
      setBusyFor(busyKey, false);
    }
  }, [refreshUser, loadTransactions, setBusyFor]);

  const addMachineCredit = useCallback((device) => runPurchase(
    `device:${device.id}`,
    () => apiService.addRemoteCredit(device.id),
    `$${CREDIT_COST.toFixed(2)} added to ${device.device_name}`
  ), [runPurchase]);

  const buyGuestPass = useCallback((guestName) => runPurchase(
    'guestPass',
    () => apiService.purchaseGuestPass({ guestName: guestName.trim() }),
    `Guest day pass bought for ${guestName.trim()}`
  ), [runPurchase]);

  const donate = useCallback((amount) => runPurchase(
    'donation',
    () => apiService.donate({ amount: parseFloat(amount) }),
    `Thank you for your $${parseFloat(amount).toFixed(2)} donation`
  ), [runPurchase]);

  // Profile writes. One field at a time, saved where the field lives.
  const saveField = useCallback(async (key, value) => {
    try {
      setBusyFor(`field:${key}`, true);
      const trimmed = typeof value === 'string' ? value.trim() : value;
      await apiService.updateUser(currentUser.id, { [key]: trimmed });

      // Saving a MatchPlay ID pulls the IFPA number across rather than making the
      // member find a separate Sync action, which is what made the three connected
      // account rows inconsistent before.
      if (key === 'matchplayUserId' && trimmed && !currentUser.ifpa_player_id) {
        try {
          const response = await apiService.get(`/users/${currentUser.id}/matchplay-profile`);
          const ifpaId = response.data?.rawProfile?.user?.ifpaId ?? response.data?.ifpaId;
          if (ifpaId) {
            await apiService.updateUser(currentUser.id, { ifpaPlayerId: String(ifpaId) });
          }
          setMatchplayProfile(response.data);
        } catch (syncError) {
          // The ID saved. Only the IFPA lookup failed, and that is not worth an
          // error toast on top of a successful save.
          console.error('MatchPlay lookup failed:', syncError);
        }
      }

      const user = await refreshUser();
      if (key === 'matchplayUserId' && !trimmed) {
        setMatchplayProfile(null);
      }
      showToast('Saved', 'success');
      return user;
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save', 'error');
      console.error(err);
      return null;
    } finally {
      setBusyFor(`field:${key}`, false);
    }
  }, [currentUser, refreshUser, setBusyFor]);

  const toggleGameDownAlerts = useCallback(async () => {
    const next = !currentUser.notify_on_game_down;
    // Optimistic: the switch has to move under the thumb, and a failed write puts
    // it back.
    setCurrentUser((prev) => ({ ...prev, notify_on_game_down: next }));
    try {
      await apiService.updateUser(currentUser.id, { notifyOnGameDown: next });
      showToast(next ? 'Game Down alerts on' : 'Game Down alerts off', 'info');
    } catch (err) {
      setCurrentUser((prev) => ({ ...prev, notify_on_game_down: !next }));
      showToast('Failed to update alerts', 'error');
      console.error(err);
    }
  }, [currentUser]);

  return {
    currentUser,
    membershipSettings,
    signedDocuments,
    onboardingStatus,
    loading,
    error,
    devices: liveDevices,
    devicesLoading,
    transactions: transactionsWithBalance,
    transactionsLoading,
    badges,
    taskStats,
    placements,
    golfSummary,
    matchplayProfile,
    assigned,
    assignedLoading,
    busy,
    balance,
    tabEnabled,
    isNegative,
    purchasesBlocked,
    addMachineCredit,
    buyGuestPass,
    donate,
    saveField,
    refreshUser,
    toggleGameDownAlerts
  };
}

export default useMyMembership;
